import type { ConsentChannel, ConsentPurpose } from '@nks/shared';
import { api } from '../../lib/api-client';

export interface Customer {
  uid: string;
  customerNo: string;
  type: string;
  name: string;
  phone: string;
  altPhone: string | null;
  email: string | null;
  idType: string | null;
  idNumber: string | null;
  tin: string | null;
  msicCode: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postcode: string | null;
  city: string | null;
  state: string | null;
  country: string;
  lifecycleStage: string;
  sourceChannel: string | null;
  notes: string | null;
  branchCode: string | null;
  createdAt: string;
}

export interface CustomerPage {
  items: Customer[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface DuplicateMatch {
  uid: string;
  name: string;
  phone: string;
  matchedOn: 'phone' | 'idNumber' | 'email' | 'name+postcode';
}

export interface CreateCustomerInput {
  name: string;
  phone: string;
  email?: string;
  idType?: string;
  idNumber?: string;
  tin?: string;
  addressLine1?: string;
  postcode?: string;
  city?: string;
  state?: string;
  notes?: string;
  confirmNotDuplicate?: boolean;
}

export function listCustomers(params: { q?: string; cursor?: string }): Promise<CustomerPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<CustomerPage>(`/customers${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getCustomer(uid: string, unmask = false): Promise<Customer> {
  return api<Customer>(`/customers/${uid}${unmask ? '?unmask=true' : ''}`);
}

export function createCustomer(input: CreateCustomerInput): Promise<Customer> {
  return api<Customer>('/customers', { method: 'POST', body: input });
}

export interface UpdateCustomerInput {
  name: string;
  phone: string;
  altPhone?: string;
  email?: string;
  idType?: string;
  /** Left out to keep the stored number; '' clears it. */
  idNumber?: string;
  tin?: string;
  msicCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  postcode?: string;
  city?: string;
  state?: string;
  notes?: string;
  confirmNotDuplicate?: boolean;
}

/**
 * Full replacement of the editable fields: a field left out is cleared, except
 * `idNumber`, which is kept (the screen never holds the raw value to send back).
 */
export function updateCustomer(uid: string, input: UpdateCustomerInput): Promise<Customer> {
  return api<Customer>(`/customers/${uid}`, { method: 'PATCH', body: input });
}

export interface ConsentRecord {
  uid: string;
  purpose: ConsentPurpose;
  /** One of CONSENT_CHANNELS, or an older free-text value captured before the list was fixed. */
  channel: string;
  granted: boolean;
  capturedAt: string;
  capturedByName: string | null;
  evidence: string | null;
}

export interface ConsentHistory {
  /** Newest first. */
  items: ConsentRecord[];
  /** True when older captures were left out (the API caps the history). */
  truncated: boolean;
}

export function listConsents(uid: string): Promise<ConsentHistory> {
  return api<ConsentHistory>(`/customers/${uid}/consents`);
}

export function captureConsent(
  uid: string,
  input: { purpose: ConsentPurpose; channel: ConsentChannel; granted: boolean; evidence?: string },
): Promise<{ uid: string }> {
  return api<{ uid: string }>(`/customers/${uid}/consents`, { method: 'POST', body: input });
}

export interface MergePreview {
  survivingUid: string;
  mergedUid: string;
  /** Rows that would move, keyed by table name. */
  moved: Record<string, number>;
}

/** What a merge would move. Changes nothing (the API runs the merge and rolls it back). */
export function previewMerge(input: { survivingUid: string; mergedUid: string }): Promise<MergePreview> {
  const search = new URLSearchParams(input);
  return api<MergePreview>(`/customers/merge/preview?${search.toString()}`);
}

export function mergeCustomers(input: {
  survivingUid: string;
  mergedUid: string;
  reason: string;
}): Promise<{ survivingUid: string; moved: Record<string, number> }> {
  return api('/customers/merge', { method: 'POST', body: input });
}

export function exportCustomersCsv(q?: string): Promise<string> {
  const search = new URLSearchParams();
  if (q !== undefined && q.length > 0) search.set('q', q);
  const qs = search.toString();
  return api<string>(`/customers/export/csv${qs.length > 0 ? `?${qs}` : ''}`);
}

// ── The customer page's module tabs ─────────────────────────────────────
//
// Each tab reads its OWN module's list, filtered by customer (CLAUDE.md §10).
// Only the fields the tabs show are typed here: the customers feature must not
// import another feature's types (§4), and the full shapes belong to the
// module that owns them.

export interface HistoryPage<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface CustomerLead {
  uid: string;
  leadNo: string;
  stage: string;
  source: string;
  interestModel: string | null;
  assignedAdvisor: string | null;
  createdAt: string;
}

export interface CustomerQuotation {
  uid: string;
  quotationNo: string;
  status: string;
  model: string | null;
  grandTotal: string;
  currency: string;
  validUntil: string;
  createdAt: string;
}

export interface CustomerBooking {
  uid: string;
  bookingNo: string;
  status: string;
  model: string | null;
  quotedBike: string | null;
  chassisNo: string | null;
  depositAmount: string;
  currency: string;
  createdAt: string;
  /** On the cashier's Till: reads "Awaiting deposit" rather than "No bike yet". */
  awaitingDeposit: boolean;
  /** CONFIRMED with no bike: an override gave it to another booking. */
  bikeTaken: boolean;
}

export interface CustomerFinancing {
  uid: string;
  applicationNo: string;
  status: string;
  bookingUid: string;
  bookingNo: string;
  providerName: string;
  amount: string;
  currency: string;
  tenureMonths: number;
  createdAt: string;
}

export interface CustomerUnit {
  uid: string;
  chassisNo: string;
  model: string | null;
  colour: string | null;
  plateNo: string | null;
  stockStatus: string;
}

export interface CustomerRegistrationCase {
  uid: string;
  caseNo: string;
  status: string;
  chassisNo: string;
  plateNo: string | null;
  model: string | null;
  currentStage: string | null;
  openedAt: string;
  closedAt: string | null;
}

export interface CustomerDelivery {
  uid: string;
  doNo: string;
  status: string;
  chassisNo: string;
  plateNo: string | null;
  model: string | null;
  scheduledAt: string | null;
  deliveredAt: string | null;
}

export interface CustomerJobCard {
  uid: string;
  jobNo: string;
  status: string;
  statusLabel: string;
  jobType: string;
  chassisNo: string;
  plateNo: string | null;
  model: string | null;
  arrivedAt: string;
  closedAt: string | null;
  isComeback: boolean;
}

export interface CustomerInvoice {
  uid: string;
  invoiceNo: string;
  status: string;
  source: string;
  jobCardUid: string | null;
  jobNo: string | null;
  grandTotal: string;
  paidTotal: string;
  balanceDue: string;
  currency: string;
  issuedAt: string | null;
}

export interface CustomerCounterOrder {
  uid: string;
  orderNo: string;
  status: string;
  invoiceUid: string | null;
  invoiceNo: string | null;
  grandTotal: string;
  createdAt: string;
}

/** One page of a module's list for one customer. */
function customerPage<T>(path: string, customerUid: string, cursor?: string): Promise<HistoryPage<T>> {
  const search = new URLSearchParams({ customerUid });
  if (cursor !== undefined && cursor.length > 0) search.set('cursor', cursor);
  return api<HistoryPage<T>>(`${path}?${search.toString()}`);
}

/** Customer service lists filter by `customer`, not `customerUid`. */
function serviceDeskPage<T>(
  path: string,
  customerUid: string,
  cursor?: string,
  extra: Record<string, string> = {},
): Promise<HistoryPage<T>> {
  const search = new URLSearchParams({ customer: customerUid, ...extra });
  if (cursor !== undefined && cursor.length > 0) search.set('cursor', cursor);
  return api<HistoryPage<T>>(`${path}?${search.toString()}`);
}

export interface CustomerCase {
  uid: string;
  caseNo: string;
  status: string;
  severity: string;
  category: string;
  subject: string;
  owner: { uid: string; name: string };
  slaResolveDueAt: string;
  breached: { response: boolean; resolve: boolean };
  createdAt: string;
}

export interface CustomerFollowUp {
  uid: string;
  type: string;
  status: string;
  dueAt: string;
  overdue: boolean;
  owner: { uid: string; name: string };
}

export interface CustomerReminder {
  uid: string;
  status: string;
  ruleCode: string;
  dueDate: string;
  dueMileage: number | null;
  unit: { uid: string; chassisNo: string; plateNo: string | null };
  sentAt: string | null;
}

/**
 * The module lists the tabs read, by query-key prefix. The prefix is the
 * owning module's own, so a merge (invalidateAfterMerge) and the module's own
 * writes refresh the tab with no extra wiring.
 */
export const CUSTOMER_HISTORY = {
  leads: (uid: string, cursor?: string) => customerPage<CustomerLead>('/leads', uid, cursor),
  quotations: (uid: string, cursor?: string) => customerPage<CustomerQuotation>('/quotations', uid, cursor),
  bookings: (uid: string, cursor?: string) => customerPage<CustomerBooking>('/bookings', uid, cursor),
  financing: (uid: string, cursor?: string) => customerPage<CustomerFinancing>('/financing', uid, cursor),
  units: (uid: string, cursor?: string) => customerPage<CustomerUnit>('/units', uid, cursor),
  'registration-cases': (uid: string, cursor?: string) =>
    customerPage<CustomerRegistrationCase>('/registration-cases', uid, cursor),
  deliveries: (uid: string, cursor?: string) => customerPage<CustomerDelivery>('/deliveries', uid, cursor),
  'job-cards': (uid: string, cursor?: string) => customerPage<CustomerJobCard>('/workshop/job-cards', uid, cursor),
  invoices: (uid: string, cursor?: string) => customerPage<CustomerInvoice>('/pos/invoices', uid, cursor),
  'counter-orders': (uid: string, cursor?: string) =>
    customerPage<CustomerCounterOrder>('/parts/counter-orders', uid, cursor),
  cases: (uid: string, cursor?: string) => serviceDeskPage<CustomerCase>('/service-desk/cases', uid, cursor),
  'follow-ups': (uid: string, cursor?: string) =>
    serviceDeskPage<CustomerFollowUp>('/service-desk/follow-ups', uid, cursor, { status: 'OPEN', mine: '0' }),
  reminders: (uid: string, cursor?: string) =>
    serviceDeskPage<CustomerReminder>('/service-desk/reminders', uid, cursor),
};

export type HistoryList = keyof typeof CUSTOMER_HISTORY;

export interface CustomerCredit {
  customerUid: string;
  /** A STRING. Money received for bookings and not yet on an invoice. */
  creditHeld: string;
  /** What the credit is made of. All STRINGS. */
  credit: { held: string; forfeited: string; refunded: string; refundable: string };
  currency: string;
}

export function fetchCustomerCredit(customerUid: string): Promise<CustomerCredit> {
  return api<CustomerCredit>(`/pos/credit?customerUid=${encodeURIComponent(customerUid)}`);
}
